import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { track } from '../lib/amplitude';
import { config } from '../lib/env';
import { cameraPreference, micPreference } from '../lib/storage';
import type { CompleteVideoResponse } from '../lib/types';
import { fixWebmDuration } from '../lib/webm-duration';
import {
  AUDIO_ONLY_BITRATE,
  SCREEN_AUDIO_BITRATE,
  detectSupport,
  pickCodec,
  type CaptureSupport,
  type CodecChoice,
  type RecordingMode,
} from './codec';
import {
  Compositor,
  DEFAULT_COMPOSITOR_SETTINGS,
  mixAudio,
  type BubbleCorner,
  type CompositorSettings,
  type MixedAudio,
  anchorFor,
} from './composer';
import {
  CaptureDenied,
  capturePermission,
  listDevices,
  requestCamera,
  requestDisplay,
  requestMic,
  stopStream,
  watchCapturePermission,
  type InputDevice,
  type MediaPermissionState,
} from './media';
import { StreamingUpload, type UploadState, type UploadTransport } from './uploader';

/**
 * The recorder state machine.
 *
 *   idle ──arm()──▶ ready ──start()──▶ countdown ──▶ recording ⇄ paused
 *                     ▲                                  │
 *                     └────────restart()─────────────────┤
 *                                                        ▼
 *                                                    finishing ──▶ preview
 *
 * The rule that everything else bends around: the source MediaStream is
 * acquired once, in `arm()`, and is not released until the user leaves the
 * recorder. Restart tears down the MediaRecorder and the in-flight upload and
 * builds new ones over the *same* stream, so the browser never asks "which
 * screen would you like to share?" twice for one recording session. That is
 * the specific thing Cap got wrong.
 *
 * Two modes run through this one machine:
 *
 *   screen:  getDisplayMedia, optional mic, optional webcam bubble. The mic is
 *            a garnish; a silent walkthrough is a perfectly good recording.
 *   audio:   getUserMedia only, no display prompt, no compositor. The mic is
 *            the *whole* source, so being denied it is a hard failure rather
 *            than a degradation, and `arm()` is where that is discovered.
 *
 * Everything after `arm()` (countdown, pause, restart, upload-while-recording,
 * the finish/preview handoff) is deliberately shared. The only divergences
 * are which stream `arm()` acquires, what `buildComposed()` assembles, and the
 * bitrate handed to MediaRecorder.
 *
 * ---- when the microphone and camera are asked for ------------------------
 *
 * Both default to on, and the choice is remembered per browser. What is *not*
 * allowed is turning that default into a permission prompt on page load.
 * Safari requires a user gesture for getUserMedia, so an ungestured call there
 * simply fails; Chrome and Firefox deprioritise ungestured prompts; and a
 * dialog somebody did not trigger gets dismissed, which is a sticky denial
 * hidden behind a padlock icon most people have never clicked.
 *
 * So the flow is:
 *
 *   granted  bring both up immediately and silently. No dialog can appear, so
 *            there is nothing to trigger and a returning user sees their own
 *            camera bubble with zero clicks.
 *   prompt   remember the intent, show it as intent, and ask on the next real
 *            gesture: the toggle, choosing a screen, or pressing record.
 *   denied   say so once, say where the switch is, and let them record without
 *            it. A silent screen recording still beats no screen recording.
 *
 * The permission state is watched rather than sampled, so granting from the
 * browser's own menu brings the source up without a reload.
 */

export type RecorderPhase =
  | 'unsupported'
  | 'idle'
  | 'arming'
  | 'ready'
  | 'countdown'
  | 'recording'
  | 'paused'
  | 'finishing'
  | 'preview';

/** Chunk cadence. Small enough to keep parts flowing, large enough to be cheap. */
const TIMESLICE_MS = 3000;
const COUNTDOWN_FROM = 3;
/** Past this we nudge; we never hard-stop somebody mid-sentence. */
export const LONG_RECORDING_MS = 55 * 60 * 1000;

/** What the picture is: a screen, one window, or the camera itself. */
export type RecordSurface = 'monitor' | 'window' | 'camera';

/** Whose library a take lands in. */
export type Workspace = 'personal' | 'team';

/**
 * What ended the take. `capture_bar` is the browser's own "Stop sharing", and
 * the default, because every path that is not a control in this app is the
 * capture going away underneath it.
 */
export type StopVia = 'button' | 'hotkey' | 'capture_bar';

export interface UseRecorderOptions {
  transport: UploadTransport;
  /** Screen capture, or a microphone-only voice note. Defaults to screen. */
  mode?: RecordingMode;
  /** For screen mode: which pane the picker opens on, or the camera as the whole picture. */
  surface?: RecordSurface;
  /** Title used at creation time; the preview screen can change it. */
  makeTitle: () => string;
  /** Where a finished take lands, reported with the upload. */
  workspace?: Workspace;
  /** Skip the 3-2-1. */
  countdown?: boolean;
}

export interface RecorderApi {
  phase: RecorderPhase;
  mode: RecordingMode;
  support: CaptureSupport;
  codec: CodecChoice | null;
  error: string | null;
  /** Milliseconds of recorded material, excluding paused time. */
  elapsedMs: number;
  countdown: number;
  upload: UploadState;
  result: CompleteVideoResponse | null;
  shareId: string | null;

  /** Video-only stream for the on-screen preview. Never contains audio. */
  monitor: MediaStream | null;
  /**
   * The screen or window on its own, before the bubble is drawn in. The
   * recording cover shows this with the live bubble laid over it, so the
   * face can still be dragged about mid take.
   */
  displayMonitor: MediaStream | null;
  cameraMonitor: MediaStream | null;
  /** The live microphone, for the input-level meter. Null when there is none. */
  micMonitor: MediaStream | null;
  hasDisplay: boolean;
  hasMic: boolean;
  hasCamera: boolean;
  /** True once the mode's required source is in hand and recording can start. */
  armed: boolean;
  micMuted: boolean;
  bubble: CompositorSettings;

  /** What this browser has already decided about each input. */
  micPermission: MediaPermissionState;
  cameraPermission: MediaPermissionState;
  /**
   * Remembered intent, which is not the same as `hasMic`/`hasCamera`: wanted
   * and not yet live is exactly the state the toggles have to show while a
   * gesture is still owed.
   */
  micWanted: boolean;
  cameraWanted: boolean;
  /** Null means "whatever the system calls default". */
  micDeviceId: string | null;
  cameraDeviceId: string | null;
  /** Populated once a permission has been granted; labels are blank before. */
  micDevices: InputDevice[];
  cameraDevices: InputDevice[];

  /** Local playback of the finished take, duration header patched. */
  previewUrl: string | null;
  previewBlob: Blob | null;

  arm: () => Promise<void>;
  toggleMic: () => Promise<void>;
  toggleCamera: () => Promise<void>;
  selectMicDevice: (deviceId: string | null) => Promise<void>;
  selectCameraDevice: (deviceId: string | null) => Promise<void>;
  /**
   * The deliberate second attempt, for somebody who has just changed the
   * browser's mind from its own menu. Never called on a timer.
   */
  requestInputs: () => Promise<void>;
  setMicMuted: (muted: boolean) => void;
  setBubble: (patch: Partial<CompositorSettings>) => void;
  moveBubble: (corner: BubbleCorner) => void;

  start: () => void;
  skipCountdown: () => void;
  pause: () => void;
  resume: () => void;
  togglePause: () => void;
  restart: () => void;
  stop: (via?: StopVia) => Promise<void>;
  discard: () => Promise<void>;
  retryUpload: () => Promise<void>;
  reset: () => void;
  releaseSources: () => void;
}

function stopRecorder(recorder: MediaRecorder): Promise<void> {
  return new Promise((resolve) => {
    if (recorder.state === 'inactive') {
      resolve();
      return;
    }
    recorder.addEventListener('stop', () => resolve(), { once: true });
    try {
      recorder.stop();
    } catch {
      resolve();
    }
  });
}

/** Seconds, to one decimal, the way the upload reports a duration. */
function durationSec(elapsedMs: number): number {
  return Math.round(elapsedMs / 100) / 10;
}

export function useRecorder(options: UseRecorderOptions): RecorderApi {
  const mode: RecordingMode = options.mode ?? 'screen';
  const support = useMemo(() => detectSupport(), []);
  const codec = useMemo(
    () => (support.mediaRecorder ? pickCodec(mode) : null),
    [mode, support.mediaRecorder],
  );
  const modeSupported = mode === 'audio' ? support.audioOk : support.screenOk;

  const [phase, setPhase] = useState<RecorderPhase>(support.ok ? 'idle' : 'unsupported');
  const [error, setError] = useState<string | null>(null);
  const [elapsedMs, setElapsedMs] = useState(0);
  const [countdown, setCountdown] = useState(0);
  const [upload, setUpload] = useState<UploadState>({
    phase: 'idle',
    videoId: null,
    shareId: null,
    uploadedBytes: 0,
    capturedBytes: 0,
    partsUploaded: 0,
    partsInFlight: 0,
    error: null,
  });
  const [result, setResult] = useState<CompleteVideoResponse | null>(null);
  const [monitor, setMonitor] = useState<MediaStream | null>(null);
  const [displayMonitor, setDisplayMonitor] = useState<MediaStream | null>(null);
  const [cameraMonitor, setCameraMonitor] = useState<MediaStream | null>(null);
  const [micMonitor, setMicMonitor] = useState<MediaStream | null>(null);
  const [hasDisplay, setHasDisplay] = useState(false);
  const [hasMic, setHasMic] = useState(false);
  const [hasCamera, setHasCamera] = useState(false);
  const [micMuted, setMicMutedState] = useState(false);
  const [bubble, setBubbleState] = useState<CompositorSettings>(DEFAULT_COMPOSITOR_SETTINGS);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewBlob, setPreviewBlob] = useState<Blob | null>(null);

  // Read once, at mount: this is what the person chose last time on this
  // browser, and nothing about it should cost a permission dialog to learn.
  const [micWanted, setMicWantedState] = useState<boolean>(() => micPreference.get().enabled);
  const [cameraWanted, setCameraWantedState] = useState<boolean>(
    () => cameraPreference.get().enabled,
  );
  const [micDeviceId, setMicDeviceIdState] = useState<string | null>(
    () => micPreference.get().deviceId,
  );
  const [cameraDeviceId, setCameraDeviceIdState] = useState<string | null>(
    () => cameraPreference.get().deviceId,
  );
  // `prompt` until the browser says otherwise, which is also the right answer
  // for every browser that will not tell us.
  const [micPermission, setMicPermissionState] = useState<MediaPermissionState>('prompt');
  const [cameraPermission, setCameraPermissionState] = useState<MediaPermissionState>('prompt');
  const [micDevices, setMicDevices] = useState<InputDevice[]>([]);
  const [cameraDevices, setCameraDevices] = useState<InputDevice[]>([]);

  const displayRef = useRef<MediaStream | null>(null);
  const micRef = useRef<MediaStream | null>(null);
  const cameraRef = useRef<MediaStream | null>(null);
  const compositorRef = useRef<Compositor | null>(null);
  const audioRef = useRef<MixedAudio | null>(null);
  const composedRef = useRef<MediaStream | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const uploaderRef = useRef<StreamingUpload | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const countdownTimerRef = useRef<number | null>(null);
  const tickRef = useRef<number | null>(null);
  const accumulatedRef = useRef(0);
  const segmentStartRef = useRef(0);
  const elapsedRef = useRef(0);
  const bubbleRef = useRef<CompositorSettings>(DEFAULT_COMPOSITOR_SETTINGS);
  const previewUrlRef = useRef<string | null>(null);
  /** Always the mode being rendered; read by callbacks that outlive a render. */
  const modeRef = useRef<RecordingMode>(mode);
  modeRef.current = mode;
  const surfaceRef = useRef<RecordSurface>(options.surface ?? 'monitor');
  surfaceRef.current = options.surface ?? 'monitor';
  /** The mode the sources currently in hand were acquired for. */
  const armedModeRef = useRef<RecordingMode>(mode);
  const optionsRef = useRef(options);
  optionsRef.current = options;

  /** Lets `arm` and the MediaRecorder error handler reach the current `stop`. */
  const stopRef = useRef<(via?: StopVia) => Promise<void>>(async () => {});

  // Preference and permission, mirrored into refs because every acquisition
  // path is async and reads them after at least one await.
  const micWantedRef = useRef(micWanted);
  const cameraWantedRef = useRef(cameraWanted);
  const micDeviceRef = useRef(micDeviceId);
  const cameraDeviceRef = useRef(cameraDeviceId);
  const micPermissionRef = useRef(micPermission);
  const cameraPermissionRef = useRef(cameraPermission);
  /** Guards against two acquisitions of one device racing each other. */
  const openingRef = useRef({ mic: false, camera: false });
  /** True between pressing record and the countdown actually starting. */
  const startingRef = useRef(false);
  /**
   * One silent attempt per teardown. Without this, a failure that leaves the
   * permission on `granted` (a microphone another app has open, say) would put
   * the effect that made the attempt straight back into making it again.
   */
  const triedSilentlyRef = useRef({ mic: false, camera: false });
  /**
   * Bumped every time sources are torn down. An acquisition that lands after
   * its teardown compares this and throws its stream away, rather than leaving
   * a camera light on for a component that has already gone.
   */
  const generationRef = useRef(0);

  // ---- clock ---------------------------------------------------------------

  const stopTick = useCallback(() => {
    if (tickRef.current !== null) {
      window.clearInterval(tickRef.current);
      tickRef.current = null;
    }
  }, []);

  const startTick = useCallback(() => {
    stopTick();
    segmentStartRef.current = performance.now();
    tickRef.current = window.setInterval(() => {
      const value = accumulatedRef.current + (performance.now() - segmentStartRef.current);
      elapsedRef.current = value;
      setElapsedMs(value);
    }, 200);
  }, [stopTick]);

  const settleTick = useCallback(() => {
    if (segmentStartRef.current > 0) {
      accumulatedRef.current += performance.now() - segmentStartRef.current;
      segmentStartRef.current = 0;
    }
    elapsedRef.current = accumulatedRef.current;
    setElapsedMs(accumulatedRef.current);
    stopTick();
  }, [stopTick]);

  /** Revoking an object URL is a side effect, so it never runs in a setState. */
  const clearPreview = useCallback(() => {
    if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
    previewUrlRef.current = null;
    setPreviewUrl(null);
    setPreviewBlob(null);
  }, []);

  const resetClock = useCallback(() => {
    stopTick();
    accumulatedRef.current = 0;
    segmentStartRef.current = 0;
    elapsedRef.current = 0;
    setElapsedMs(0);
  }, [stopTick]);

  // ---- pipeline ------------------------------------------------------------

  /**
   * (Re)build the stream MediaRecorder will consume. Safe to call whenever a
   * source is added or removed while *not* recording; never called during a
   * recording, because MediaRecorder cannot change its input mid-flight.
   */
  const buildComposed = useCallback((): MediaStream | null => {
    // Audio-only is the degenerate case of the same pipeline: one source, one
    // track, no mixing and no canvas. Handing the mic track straight to
    // MediaRecorder keeps the graph out of the way entirely.
    if (modeRef.current === 'audio') {
      audioRef.current?.close();
      audioRef.current = null;
      const track = micRef.current?.getAudioTracks()[0];
      if (!track) return null;
      const composed = new MediaStream([track]);
      composedRef.current = composed;
      // There is nothing to look at; the stage renders a level meter instead.
      setMonitor(null);
    setDisplayMonitor(null);
      return composed;
    }

    const display = displayRef.current;
    if (!display) return null;
    const displayTrack = display.getVideoTracks()[0];
    setDisplayMonitor((previous) =>
      surfaceRef.current === 'camera' || !displayTrack
        ? null
        : previous?.getVideoTracks()[0] === displayTrack
          ? previous
          : new MediaStream([displayTrack]),
    );

    audioRef.current?.close();
    const audio = mixAudio([display, micRef.current]);
    audioRef.current = audio;

    let videoTrack: MediaStreamTrack | undefined;

    if (cameraRef.current && surfaceRef.current !== 'camera') {
      if (compositorRef.current) {
        compositorRef.current.setCamera(cameraRef.current);
      } else {
        compositorRef.current = new Compositor(display, cameraRef.current);
        compositorRef.current.update(bubbleRef.current);
      }
      videoTrack = compositorRef.current.captureStream().getVideoTracks()[0];
    } else {
      compositorRef.current?.stop();
      compositorRef.current = null;
      videoTrack = display.getVideoTracks()[0];
    }

    if (!videoTrack) return null;

    const composed = new MediaStream(audio.track ? [videoTrack, audio.track] : [videoTrack]);
    composedRef.current = composed;
    // The monitor is video-only: playing the mixed audio back through the page
    // would feed the microphone into itself.
    setMonitor(new MediaStream([videoTrack]));
    return composed;
  }, []);

  const teardownRecorder = useCallback(async (dropTail: boolean) => {
    const recorder = recorderRef.current;
    recorderRef.current = null;
    if (!recorder) return;
    if (dropTail) recorder.ondataavailable = null;
    await stopRecorder(recorder);
  }, []);

  const releaseSources = useCallback(() => {
    generationRef.current += 1;
    triedSilentlyRef.current = { mic: false, camera: false };
    stopTick();
    if (countdownTimerRef.current !== null) {
      window.clearInterval(countdownTimerRef.current);
      countdownTimerRef.current = null;
    }
    compositorRef.current?.stop();
    compositorRef.current = null;
    audioRef.current?.close();
    audioRef.current = null;
    stopStream(displayRef.current);
    stopStream(micRef.current);
    stopStream(cameraRef.current);
    displayRef.current = null;
    micRef.current = null;
    cameraRef.current = null;
    composedRef.current = null;
    setMonitor(null);
    setDisplayMonitor(null);
    setCameraMonitor(null);
    setMicMonitor(null);
    setHasDisplay(false);
    setHasMic(false);
    setHasCamera(false);
    setMicMutedState(false);
  }, [stopTick]);

  // ---- sources -------------------------------------------------------------

  /**
   * "Stop sharing" from the browser's own capture bar, a mic yanked out of its
   * socket, an OS-level device change: all of them end the track. If we are
   * recording, that means "I'm done": save what we have rather than dropping
   * it on the floor.
   */
  const onSourceEnded = useCallback(() => {
    const live = recorderRef.current;
    if (live && live.state !== 'inactive') {
      void stopRef.current('capture_bar');
    } else {
      releaseSources();
      setPhase('idle');
    }
  }, [releaseSources]);

  /** Permission state, in state and in a ref, from one place. */
  const noteMicPermission = useCallback((state: MediaPermissionState) => {
    micPermissionRef.current = state;
    setMicPermissionState(state);
  }, []);

  const noteCameraPermission = useCallback((state: MediaPermissionState) => {
    cameraPermissionRef.current = state;
    setCameraPermissionState(state);
  }, []);

  /** Intent, in state, in a ref, and on this device for next time. */
  const rememberMic = useCallback((enabled: boolean, deviceId: string | null) => {
    micWantedRef.current = enabled;
    micDeviceRef.current = deviceId;
    setMicWantedState(enabled);
    setMicDeviceIdState(deviceId);
    micPreference.set({ enabled, deviceId });
  }, []);

  const rememberCamera = useCallback((enabled: boolean, deviceId: string | null) => {
    cameraWantedRef.current = enabled;
    cameraDeviceRef.current = deviceId;
    setCameraWantedState(enabled);
    setCameraDeviceIdState(deviceId);
    cameraPreference.set({ enabled, deviceId });
  }, []);

  /**
   * Every microphone acquisition in the hook goes through here, so that the
   * error copy, the permission bookkeeping and the two ways an acquisition can
   * be stale are written once rather than three times.
   *
   * Returns whether the microphone is now live.
   */
  const openMic = useCallback(
    async (deviceId: string | null): Promise<boolean> => {
      if (openingRef.current.mic) return false;
      openingRef.current.mic = true;
      const generation = generationRef.current;
      try {
        const mic = await requestMic(deviceId);
        // Somebody released the sources, or left, while the browser was asking.
        if (generation !== generationRef.current) {
          stopStream(mic);
          return false;
        }
        stopStream(micRef.current);
        micRef.current = mic;
        setHasMic(true);
        setMicMutedState(false);
        setMicMonitor(mic);
        // Getting a stream is the most reliable "granted" there is, and the
        // only one Safari gives us at all.
        noteMicPermission('granted');
        // Only audio-only mode treats a vanished microphone as the end of the
        // take. In screen mode the picture carries on without it.
        if (modeRef.current === 'audio') {
          mic.getAudioTracks()[0]?.addEventListener('ended', onSourceEnded);
        }
        return true;
      } catch (cause) {
        if (generation !== generationRef.current) return false;
        if (cause instanceof CaptureDenied) {
          setError(cause.message);
          if (cause.reason === 'denied') noteMicPermission('denied');
        } else {
          setError('Could not start the microphone.');
        }
        return false;
      } finally {
        openingRef.current.mic = false;
      }
    },
    [noteMicPermission, onSourceEnded],
  );

  /** The camera's half of the same story. */
  const openCamera = useCallback(
    async (deviceId: string | null): Promise<boolean> => {
      if (openingRef.current.camera) return false;
      if (!support.canvasCapture) {
        setError('This browser cannot composite a webcam bubble. Record without it, or use Chrome.');
        return false;
      }
      openingRef.current.camera = true;
      const generation = generationRef.current;
      try {
        const stream = await requestCamera(deviceId);
        if (generation !== generationRef.current) {
          stopStream(stream);
          return false;
        }
        stopStream(cameraRef.current);
        cameraRef.current = stream;
        setHasCamera(true);
        setCameraMonitor(stream);
        noteCameraPermission('granted');
        return true;
      } catch (cause) {
        if (generation !== generationRef.current) return false;
        if (cause instanceof CaptureDenied) {
          setError(cause.message);
          if (cause.reason === 'denied') noteCameraPermission('denied');
        } else {
          setError('Could not start the camera.');
        }
        return false;
      } finally {
        openingRef.current.camera = false;
      }
    },
    [noteCameraPermission, support.canvasCapture],
  );

  /** Rebuild the recorder's input, when there is anything to build it from. */
  const rebuild = useCallback(() => {
    if (modeRef.current === 'audio' || displayRef.current) buildComposed();
  }, [buildComposed]);

  /**
   * Bring up whatever was asked for but is not live yet.
   *
   * Called from real gestures only: pressing a toggle, choosing a screen,
   * pressing record. `force` is the one deliberate retry after somebody has
   * changed a denial in the browser's own menu; without it a denied input is
   * left alone, because asking again would only be asking again.
   *
   * Returns whether anything actually came up, so callers can leave a working
   * pipeline alone rather than tearing the audio graph down and rebuilding it
   * identically on the way into a recording.
   */
  const requestPending = useCallback(
    async (force = false): Promise<boolean> => {
      // In audio-only mode the microphone is `arm()`'s, not a garnish.
      if (modeRef.current === 'audio') return false;
      let changed = false;
      if (
        micWantedRef.current &&
        !micRef.current &&
        (force || micPermissionRef.current !== 'denied')
      ) {
        changed = (await openMic(micDeviceRef.current)) || changed;
      }
      if (
        cameraWantedRef.current &&
        !cameraRef.current &&
        support.canvasCapture &&
        (force || cameraPermissionRef.current !== 'denied')
      ) {
        changed = (await openCamera(cameraDeviceRef.current)) || changed;
      }
      return changed;
    },
    [openCamera, openMic, support.canvasCapture],
  );

  const arm = useCallback(async () => {
    if (!modeSupported) return;

    // Audio-only: the microphone is the entire recording, so this is both the
    // source picker and the permission gate. A denial leaves us in `idle` with
    // an error and no way to press record, which is the point: a voice note
    // with no voice is not a degraded recording, it is an empty file.
    if (modeRef.current === 'audio') {
      setError(null);
      setPhase('arming');
      // Re-arming swaps microphones; drop the old one first so the device is
      // released rather than held open by a stream nobody reads.
      stopStream(micRef.current);
      micRef.current = null;
      setHasMic(false);
      setMicMonitor(null);
      if (!(await openMic(micDeviceRef.current))) {
        setPhase('idle');
        return;
      }
      buildComposed();
      setPhase('ready');
      return;
    }

    setError(null);
    setPhase('arming');
    if (surfaceRef.current === 'camera') {
      // The camera is the whole picture: no picker, no bubble.
      if (!cameraRef.current && !(await openCamera(cameraDeviceRef.current))) {
        setError('The camera could not be opened.');
        setPhase('idle');
        return;
      }
      displayRef.current = cameraRef.current;
      setHasDisplay(true);
      await requestPending();
      buildComposed();
      setPhase('ready');
      return;
    }
    try {
      const display = await requestDisplay(surfaceRef.current === 'window' ? 'window' : 'monitor');
      displayRef.current = display;
      setHasDisplay(true);

      const [track] = display.getVideoTracks();
      track?.addEventListener('ended', onSourceEnded);
    } catch (cause) {
      setError(cause instanceof CaptureDenied ? cause.message : 'Could not start screen capture.');
      track('Recording Failed', {
        stage: 'start',
        reason: cause instanceof CaptureDenied ? cause.reason : 'unknown',
      });
      setPhase('idle');
      return;
    }

    // Picking a screen is a gesture, and it is the moment somebody has already
    // decided to record, so it is a fair moment to ask for the rest. A refusal
    // here is not fatal: the screen is in hand either way, and the pipeline is
    // built from whatever did come up.
    await requestPending();
    buildComposed();
    setPhase('ready');
  }, [buildComposed, modeSupported, onSourceEnded, openCamera, openMic, requestPending]);

  const toggleMic = useCallback(async () => {
    if (phase === 'recording' || phase === 'paused' || phase === 'countdown') return;
    // In audio-only mode the mic is not a toggle: it is the source, and
    // `arm()` owns it. Turning it off here would leave nothing to record.
    if (modeRef.current === 'audio') {
      await arm();
      return;
    }
    setError(null);
    if (micRef.current) {
      stopStream(micRef.current);
      micRef.current = null;
      setHasMic(false);
      setMicMutedState(false);
      setMicMonitor(null);
      rememberMic(false, micDeviceRef.current);
    } else {
      // The click is the gesture the browser wants, and the reason nothing was
      // asked for on load.
      rememberMic(true, micDeviceRef.current);
      if (!(await openMic(micDeviceRef.current))) return;
    }
    track('Setting Changed', { setting: 'mic', value: Boolean(micRef.current) });
    rebuild();
  }, [arm, openMic, phase, rebuild, rememberMic]);

  const toggleCamera = useCallback(async () => {
    if (phase === 'recording' || phase === 'paused' || phase === 'countdown') return;
    // A webcam bubble needs a frame to sit in. There isn't one.
    if (modeRef.current === 'audio') return;
    setError(null);
    if (cameraRef.current) {
      stopStream(cameraRef.current);
      cameraRef.current = null;
      setHasCamera(false);
      setCameraMonitor(null);
      rememberCamera(false, cameraDeviceRef.current);
    } else {
      rememberCamera(true, cameraDeviceRef.current);
      if (!(await openCamera(cameraDeviceRef.current))) return;
    }
    track('Setting Changed', { setting: 'camera', value: Boolean(cameraRef.current) });
    rebuild();
  }, [openCamera, phase, rebuild, rememberCamera]);

  /**
   * Choosing a named device. Remembered as the thing to ask for next time, and
   * swapped in now if that input is already live.
   */
  const selectMicDevice = useCallback(
    async (deviceId: string | null) => {
      if (phase === 'recording' || phase === 'paused' || phase === 'countdown') return;
      rememberMic(micWantedRef.current, deviceId);
      if (!micRef.current) return;
      setError(null);
      stopStream(micRef.current);
      micRef.current = null;
      setHasMic(false);
      setMicMonitor(null);
      if (await openMic(deviceId)) rebuild();
    },
    [openMic, phase, rebuild, rememberMic],
  );

  const selectCameraDevice = useCallback(
    async (deviceId: string | null) => {
      if (phase === 'recording' || phase === 'paused' || phase === 'countdown') return;
      rememberCamera(cameraWantedRef.current, deviceId);
      if (!cameraRef.current) return;
      setError(null);
      stopStream(cameraRef.current);
      cameraRef.current = null;
      setHasCamera(false);
      setCameraMonitor(null);
      if (await openCamera(deviceId)) rebuild();
    },
    [openCamera, phase, rebuild, rememberCamera],
  );

  /**
   * "I have allowed it now, try again." The only path that asks a second time,
   * and it exists because Safari has no Permissions API to tell us the answer
   * changed, so somebody who has just fixed their site settings would otherwise
   * have to reload the page to be heard.
   */
  const requestInputs = useCallback(async () => {
    if (phase === 'recording' || phase === 'paused' || phase === 'countdown') return;
    setError(null);
    // Chromium and Firefox are the authority here and may already have moved on
    // from the denial; Safari answers `prompt`, which is the same instruction.
    const [mic, camera] = await Promise.all([
      capturePermission('microphone'),
      capturePermission('camera'),
    ]);
    noteMicPermission(mic);
    noteCameraPermission(camera);
    if (modeRef.current === 'audio') {
      await arm();
      return;
    }
    if (await requestPending(true)) rebuild();
  }, [arm, noteCameraPermission, noteMicPermission, phase, rebuild, requestPending]);

  const setMicMuted = useCallback((muted: boolean) => {
    // Works mid-recording: a disabled track feeds silence into the mix rather
    // than disappearing, so the audio stays continuous.
    for (const track of micRef.current?.getAudioTracks() ?? []) track.enabled = !muted;
    setMicMutedState(muted);
  }, []);

  const setBubble = useCallback((patch: Partial<CompositorSettings>) => {
    const next = { ...bubbleRef.current, ...patch };
    bubbleRef.current = next;
    compositorRef.current?.update(next);
    setBubbleState(next);
  }, []);

  const moveBubble = useCallback(
    (corner: BubbleCorner) => setBubble({ corner, anchor: anchorFor(corner) }),
    [setBubble],
  );

  // ---- recording -----------------------------------------------------------

  const beginRecording = useCallback(() => {
    const audioOnly = modeRef.current === 'audio';
    const composed = composedRef.current ?? buildComposed();
    if (!composed || !codec) {
      setError(
        audioOnly
          ? 'The recorder could not start. Choose your microphone again.'
          : 'The recorder could not start. Try picking your screen again.',
      );
      track('Recording Failed', { stage: 'start', reason: 'no_source' });
      setPhase('ready');
      return;
    }

    chunksRef.current = [];
    setResult(null);
    clearPreview();
    resetClock();

    const uploader = new StreamingUpload(optionsRef.current.transport, setUpload);
    uploaderRef.current = uploader;
    // Not awaited: recording starts now, the session opens alongside it, and
    // chunks buffer in memory for the fraction of a second in between.
    void uploader
      .start({
        contentType: codec.contentType,
        title: optionsRef.current.makeTitle(),
        partCount: 3,
      })
      .catch(() => {
        // Surfaced through the upload state; the take itself is unaffected and
        // can still be downloaded from the preview screen.
      });

    // Speech at 96kbps Opus is transparent; asking for a video bitrate on a
    // stream with no video track makes some encoders unhappy, so don't.
    const recorderOptions: MediaRecorderOptions = audioOnly
      ? { audioBitsPerSecond: AUDIO_ONLY_BITRATE }
      : { videoBitsPerSecond: config.recorderBitrate, audioBitsPerSecond: SCREEN_AUDIO_BITRATE };
    if (codec.mimeType) recorderOptions.mimeType = codec.mimeType;

    let recorder: MediaRecorder;
    try {
      recorder = new MediaRecorder(composed, recorderOptions);
    } catch {
      try {
        recorder = new MediaRecorder(composed);
      } catch {
        setError('This browser refused to start a recording. Try Chrome, Edge, or Firefox.');
        track('Recording Failed', { stage: 'start', reason: 'unsupported' });
        setPhase('ready');
        return;
      }
    }

    recorder.ondataavailable = (event: BlobEvent) => {
      if (!event.data || event.data.size === 0) return;
      chunksRef.current.push(event.data);
      uploaderRef.current?.push(event.data);
    };
    recorder.onerror = () => {
      setError('The recorder stopped unexpectedly. Whatever was captured up to now is kept.');
      track('Recording Failed', { stage: 'capture', reason: 'recorder_error' });
      void stopRef.current();
    };

    recorderRef.current = recorder;
    recorder.start(TIMESLICE_MS);
    startTick();
    setPhase('recording');
    track('Recording Started', {
      source: audioOnly
        ? 'audio'
        : surfaceRef.current === 'camera'
          ? 'camera'
          : surfaceRef.current === 'window'
            ? 'window'
            : 'screen',
      mic: Boolean(micRef.current),
      camera: !audioOnly && surfaceRef.current !== 'camera' && Boolean(cameraRef.current),
      system_audio: (displayRef.current?.getAudioTracks().length ?? 0) > 0,
    });
  }, [buildComposed, clearPreview, codec, resetClock, startTick]);

  const clearCountdown = useCallback(() => {
    if (countdownTimerRef.current !== null) {
      window.clearInterval(countdownTimerRef.current);
      countdownTimerRef.current = null;
    }
    setCountdown(0);
  }, []);

  const runCountdown = useCallback(() => {
    clearCountdown();
    setPhase('countdown');
    setCountdown(COUNTDOWN_FROM);

    // The remaining count lives in a local rather than in the state updater:
    // starting a recording is a side effect, and side effects do not belong
    // inside a setState callback.
    let remaining = COUNTDOWN_FROM;
    countdownTimerRef.current = window.setInterval(() => {
      remaining -= 1;
      if (remaining <= 0) {
        clearCountdown();
        beginRecording();
      } else {
        setCountdown(remaining);
      }
    }, 1000);
  }, [beginRecording, clearCountdown]);

  const start = useCallback(() => {
    // `startingRef` covers the gap the permission prompt opens up: the phase is
    // still `ready` while somebody reads the dialog, and pressing record twice
    // in that window must not queue two countdowns.
    if (phase !== 'ready' || startingRef.current) return;
    startingRef.current = true;
    setError(null);
    // Pressing record is the last honest moment to ask for a microphone
    // somebody has had switched on since their last recording but never
    // granted on this device. The countdown waits for the answer, which is the
    // right way round: a prompt over a running take would be worse.
    void (async () => {
      try {
        if (await requestPending()) rebuild();
        if (optionsRef.current.countdown === false) beginRecording();
        else runCountdown();
      } finally {
        startingRef.current = false;
      }
    })();
  }, [beginRecording, phase, requestPending, runCountdown]);

  const skipCountdown = useCallback(() => {
    if (phase !== 'countdown') return;
    track('Countdown Skipped');
    clearCountdown();
    beginRecording();
  }, [beginRecording, clearCountdown, phase]);

  const pause = useCallback(() => {
    const recorder = recorderRef.current;
    if (!recorder || recorder.state !== 'recording') return;
    recorder.pause();
    settleTick();
    setPhase('paused');
    track('Recording Paused');
  }, [settleTick]);

  const resume = useCallback(() => {
    const recorder = recorderRef.current;
    if (!recorder || recorder.state !== 'paused') return;
    recorder.resume();
    startTick();
    setPhase('recording');
    track('Recording Resumed');
  }, [startTick]);

  const togglePause = useCallback(() => {
    const recorder = recorderRef.current;
    if (!recorder) return;
    if (recorder.state === 'recording') pause();
    else if (recorder.state === 'paused') resume();
  }, [pause, resume]);

  /**
   * Scrap this take and immediately start another. The captured source (the
   * screen, or the microphone) is untouched, so there is no second permission
   * prompt. This is the whole point of holding the MediaStream outside the
   * MediaRecorder's lifetime, and it is identical in both modes: audio-only
   * simply has less to preserve.
   */
  const restart = useCallback(() => {
    if (phase !== 'recording' && phase !== 'paused') return;
    track('Recording Restarted');
    void (async () => {
      clearCountdown();
      await teardownRecorder(true);
      uploaderRef.current?.abort();
      uploaderRef.current = null;
      chunksRef.current = [];
      resetClock();
      setError(null);
      beginRecording();
    })();
  }, [beginRecording, clearCountdown, phase, resetClock, teardownRecorder]);

  const stop = useCallback(async (via: StopVia = 'capture_bar') => {
    if (phase !== 'recording' && phase !== 'paused') return;
    setPhase('finishing');
    clearCountdown();
    await teardownRecorder(false);
    settleTick();

    const durationMs = elapsedRef.current;
    const chunks = chunksRef.current;
    track('Recording Stopped', { duration_sec: durationSec(durationMs), stop: via });

    // Local preview first: whatever happens to the upload, the take exists and
    // can be played and downloaded from this screen.
    if (chunks.length > 0 && codec) {
      const raw = new Blob(chunks, { type: codec.contentType });
      const patched = await fixWebmDuration(raw, durationMs);
      const url = URL.createObjectURL(patched);
      previewUrlRef.current = url;
      setPreviewBlob(patched);
      setPreviewUrl(url);
    }

    const uploader = uploaderRef.current;
    if (uploader) {
      try {
        const completed = await uploader.finish({
          durationSec: durationSec(durationMs),
        });
        setResult(completed);
        track('Recording Uploaded', {
          video_id: completed.shareId,
          duration_sec: durationSec(durationMs),
          size_bytes: uploader.snapshot.capturedBytes,
          workspace: optionsRef.current.workspace ?? 'personal',
        });
      } catch {
        // Reported through upload.error; the preview screen offers a retry and
        // a download.
        track('Recording Failed', { stage: 'upload', reason: 'upload_failed' });
      }
    }
    setPhase('preview');
  }, [clearCountdown, codec, phase, settleTick, teardownRecorder]);

  stopRef.current = stop;

  const retryUpload = useCallback(async () => {
    const uploader = uploaderRef.current;
    if (!uploader) return;
    track('Upload Retried');
    await uploader.retry();
    if (uploader.snapshot.error) return;
    try {
      const completed = await uploader.finish({
        durationSec: durationSec(elapsedRef.current),
      });
      setResult(completed);
      track('Recording Uploaded', {
        video_id: completed.shareId,
        duration_sec: durationSec(elapsedRef.current),
        size_bytes: uploader.snapshot.capturedBytes,
        workspace: optionsRef.current.workspace ?? 'personal',
      });
    } catch {
      // Still failing; the state carries the reason.
      track('Recording Failed', { stage: 'upload', reason: 'upload_failed' });
    }
  }, []);

  /** Is the mode's required source still in hand? Decides ready vs idle. */
  const stillArmed = useCallback(
    () => Boolean(modeRef.current === 'audio' ? micRef.current : displayRef.current),
    [],
  );

  /** Throw the take away *and* delete the server-side video. */
  const discard = useCallback(async () => {
    track('Recording Discarded', { duration_sec: durationSec(elapsedRef.current) });
    uploaderRef.current?.abort();
    uploaderRef.current = null;
    chunksRef.current = [];
    setResult(null);
    clearPreview();
    resetClock();
    setError(null);
    setPhase(stillArmed() ? 'ready' : 'idle');
  }, [clearPreview, resetClock, stillArmed]);

  /** Keep the take that was just saved; clear the screen for the next one. */
  const reset = useCallback(() => {
    chunksRef.current = [];
    uploaderRef.current = null;
    setResult(null);
    clearPreview();
    resetClock();
    setError(null);
    setPhase(stillArmed() ? 'ready' : 'idle');
  }, [clearPreview, resetClock, stillArmed]);

  // ---- lifecycle -----------------------------------------------------------

  /**
   * Find out where we stand, and keep finding out. This is a query, not a
   * request: nothing here can put a dialog in front of anybody. Where the
   * Permissions API is missing or refuses the descriptor, the callback never
   * fires and both stay on `prompt`, which is the branch that waits politely
   * for a gesture.
   */
  useEffect(() => {
    const stopMic = watchCapturePermission('microphone', noteMicPermission);
    const stopCamera = watchCapturePermission('camera', noteCameraPermission);
    return () => {
      stopMic();
      stopCamera();
    };
  }, [noteCameraPermission, noteMicPermission]);

  /**
   * The device lists behind the pickers. Labels are blank until a permission
   * has been granted at least once, so this re-runs when one is, and again
   * whenever hardware is plugged in or pulled out.
   */
  useEffect(() => {
    let cancelled = false;
    const refresh = (): void => {
      void listDevices('audioinput').then((list) => {
        if (!cancelled) setMicDevices(list);
      });
      void listDevices('videoinput').then((list) => {
        if (!cancelled) setCameraDevices(list);
      });
    };
    refresh();
    const devices = navigator.mediaDevices;
    devices?.addEventListener('devicechange', refresh);
    return () => {
      cancelled = true;
      devices?.removeEventListener('devicechange', refresh);
    };
  }, [hasCamera, hasMic, cameraPermission, micPermission]);

  /**
   * The zero-click path, and the only place getUserMedia is called without a
   * gesture behind it. It runs exclusively on `granted`, where the browser has
   * already made its decision and no dialog can appear, so a returning user
   * lands with their microphone live and their camera bubble on the stage
   * having pressed nothing at all.
   *
   * `prompt` and `denied` deliberately fall through and do nothing.
   */
  useEffect(() => {
    if (phase !== 'idle' && phase !== 'ready') return;

    if (mode === 'audio') {
      // Audio-only arms itself, since the microphone *is* the arming step.
      if (phase !== 'idle' || micPermission !== 'granted') return;
      if (micRef.current || triedSilentlyRef.current.mic) return;
      triedSilentlyRef.current.mic = true;
      void arm();
      return;
    }

    if (micPermission === 'granted' && micWanted && !micRef.current && !triedSilentlyRef.current.mic) {
      triedSilentlyRef.current.mic = true;
      void openMic(micDeviceRef.current).then((ok) => {
        if (ok) rebuild();
      });
    }
    if (
      cameraPermission === 'granted' &&
      cameraWanted &&
      support.canvasCapture &&
      !cameraRef.current &&
      !triedSilentlyRef.current.camera
    ) {
      triedSilentlyRef.current.camera = true;
      void openCamera(cameraDeviceRef.current).then((ok) => {
        if (ok) rebuild();
      });
    }
  }, [
    arm,
    cameraPermission,
    cameraWanted,
    micPermission,
    micWanted,
    mode,
    openCamera,
    openMic,
    phase,
    rebuild,
    support.canvasCapture,
  ]);

  /**
   * Switching modes throws the sources away. Screen capture and a microphone
   * are not interchangeable, and holding a screen share open while somebody
   * records a voice note would keep the browser's "sharing your screen" bar up
   * for no reason. The UI only offers the switch from idle, ready or arming,
   * so this can never interrupt a take.
   */
  useEffect(() => {
    if (armedModeRef.current === mode) return;
    armedModeRef.current = mode;
    releaseSources();
    clearPreview();
    resetClock();
    setResult(null);
    setError(null);
    setPhase(support.ok ? 'idle' : 'unsupported');
  }, [clearPreview, mode, releaseSources, resetClock, support.ok]);

  useEffect(() => {
    return () => {
      const recorder = recorderRef.current;
      if (recorder && recorder.state !== 'inactive') {
        recorder.ondataavailable = null;
        try {
          recorder.stop();
        } catch {
          // Already gone.
        }
      }
      releaseSources();
    };
  }, [releaseSources]);

  // The last preview URL outlives every explicit clear; release it on unmount.
  useEffect(() => {
    return () => {
      if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
    };
  }, []);

  useEffect(() => {
    if (phase !== 'recording' && phase !== 'paused' && phase !== 'finishing') return;
    const guard = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', guard);
    return () => window.removeEventListener('beforeunload', guard);
  }, [phase]);

  return {
    phase,
    mode,
    support,
    codec,
    error,
    elapsedMs,
    countdown,
    upload,
    result,
    shareId: result?.shareId ?? upload.shareId,
    monitor,
    displayMonitor,
    cameraMonitor,
    micMonitor,
    hasDisplay,
    hasMic,
    hasCamera,
    armed: mode === 'audio' ? hasMic : hasDisplay,
    micMuted,
    bubble,
    micPermission,
    cameraPermission,
    micWanted,
    cameraWanted,
    micDeviceId,
    cameraDeviceId,
    micDevices,
    cameraDevices,
    previewUrl,
    previewBlob,
    arm,
    toggleMic,
    toggleCamera,
    selectMicDevice,
    selectCameraDevice,
    requestInputs,
    setMicMuted,
    setBubble,
    moveBubble,
    start,
    skipCountdown,
    pause,
    resume,
    togglePause,
    restart,
    stop,
    discard,
    retryUpload,
    reset,
    releaseSources,
  };
}

/** The machine plus the mode it is capturing in. */
export interface RecorderSession {
  recorder: RecorderApi;
  mode: RecordingMode;
  setMode: (mode: RecordingMode) => void;
  /** For screen mode: what the picture is. */
  surface: RecordSurface;
  setSurface: (surface: RecordSurface) => void;
}

/**
 * A session a *caller* owns, rather than one `<Recorder>` keeps to itself.
 *
 * /new needs this: it is a full screen mode with its own way out, and it cannot
 * decide whether closing costs somebody a take without knowing which phase the
 * machine is in. Pages with no such question keep using `<Recorder>`, which
 * opens a session of its own and releases it when it unmounts.
 */
export function useRecorderSession(options: Omit<UseRecorderOptions, 'mode' | 'surface'>): RecorderSession {
  // Phones have no getDisplayMedia and never will (that is an OS limitation,
  // not a browser one), but they do have a microphone, so a session opens on
  // audio rather than on a dead end.
  const [mode, setMode] = useState<RecordingMode>(() =>
    detectSupport().screenOk ? 'screen' : 'audio',
  );
  const [surface, setSurface] = useState<RecordSurface>('monitor');
  const recorder = useRecorder({ ...options, mode, surface });
  return { recorder, mode, setMode, surface, setSurface };
}
